<template>
    <div class="wysiwyg-editor-wrapper"
         :class="{ 'is-dragover': isDragover, 'is-focused': isFocused, 'is-disabled': disabled, 'mode-description': mode === 'description' }"
         :style="minHeight ? { '--wysiwyg-min-height': minHeight } : null"
         @dragover.prevent="handleDragOver"
         @dragleave.prevent="handleDragLeave"
         @drop.prevent="handleDrop">

        <editor-content :editor="editor" class="wysiwyg-editor-container" />

        <div v-if="attachedFiles.length > 0" class="attached-files-preview">
            <div v-for="(file, index) in attachedFiles" :key="file.id"
                 class="attached-file-item is-document"
                 @click="previewFile(file)">
                <i :class="file.icon" class="attached-file-icon"></i>
                <span class="attached-file-name">{{ file.name }}</span>
                <button type="button" class="attached-file-remove" @click.stop="removeAttachedFile(index)" title="Rimuovi">
                    <i class="fa fa-times"></i>
                </button>
            </div>
        </div>

        <div class="wysiwyg-bottom-toolbar">
            <div class="toolbar-actions">
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('bold') }" @mousedown.prevent="formatBold" title="Grassetto (Cmd+B)">
                    <strong>B</strong>
                </button>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('italic') }" @mousedown.prevent="formatItalic" title="Corsivo (Cmd+I)">
                    <em>I</em>
                </button>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('underline') }" @mousedown.prevent="formatUnderline" title="Sottolineato (Cmd+U)">
                    <u>U</u>
                </button>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('strike') }" @mousedown.prevent="formatStrike" title="Barrato">
                    <s>S</s>
                </button>
                <span class="toolbar-sep"></span>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('bulletList') }" @mousedown.prevent="formatBulletList" title="Elenco puntato">
                    <i class="fa fa-list-ul"></i>
                </button>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('orderedList') }" @mousedown.prevent="formatOrderedList" title="Elenco numerato">
                    <i class="fa fa-list-ol"></i>
                </button>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('taskList') }" @mousedown.prevent="formatTaskList" title="Checklist">
                    <i class="fa fa-check-square-o"></i>
                </button>
                <span class="toolbar-sep"></span>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('blockquote') }" @mousedown.prevent="formatBlockquote" title="Citazione">
                    <i class="fa fa-quote-left"></i>
                </button>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('codeBlock') }" @mousedown.prevent="formatCodeBlock" title="Blocco codice">
                    <i class="fa fa-code"></i>
                </button>
                <span class="toolbar-sep"></span>
                <button type="button" class="toolbar-icon-btn" :class="{ 'is-active': isActive('link') }" @mousedown.prevent="insertLink" title="Inserisci link">
                    <i class="fa fa-link"></i>
                </button>
                <button v-if="taskId" type="button" class="toolbar-icon-btn" @mousedown.prevent="triggerFileUpload" title="Allega file">
                    <i class="fa fa-paperclip"></i>
                </button>
            </div>
        </div>

        <input
            ref="fileInput"
            type="file"
            multiple
            style="display: none"
            @change="handleFileSelect"
            accept=".pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.ods,.csv,.tsv,.ppt,.pptx,.odp,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.mov,.avi,.mkv,.webm,.txt,.md,.json,.xml,.yml,.yaml,.zip,.rar,.7z,.tar,.gz,.tgz" />

        <div v-if="isDragover" class="drag-overlay">
            <i class="fa fa-cloud-upload"></i>
            <span>Rilascia per allegare</span>
        </div>

        <div v-if="uploadProgress.active" class="upload-indicator">
            <i class="fa fa-spinner fa-spin"></i>
            <span v-if="uploadProgress.total > 1">Caricamento {{ uploadProgress.done }}/{{ uploadProgress.total }}...</span>
            <span v-else>Caricamento...</span>
        </div>
    </div>
</template>

<script>
var uploadUtils = require('../../utils/taskAttachmentUpload');
var uploadTaskAttachmentsSequential = uploadUtils.uploadTaskAttachmentsSequential;
var filesFromFileList = uploadUtils.filesFromFileList;
var pasteUtils = require('../../utils/normalizePastedHtml');
var normalizePastedHtml = pasteUtils.normalizePastedHtml;
var extractDataImages = pasteUtils.extractDataImages;
var escapeAttr = pasteUtils.escapeAttr;

var tiptap = require('@tiptap/vue-2');
var Editor = tiptap.Editor;
var EditorContent = tiptap.EditorContent;
var Node = tiptap.Node;
var Extension = tiptap.Extension;
var mergeAttributes = tiptap.mergeAttributes;

var StarterKit = require('@tiptap/starter-kit').default;
var Link = require('@tiptap/extension-link').default;
var Image = require('@tiptap/extension-image').default;
var Placeholder = require('@tiptap/extension-placeholder').default;
var Underline = require('@tiptap/extension-underline').default;
var Mention = require('@tiptap/extension-mention').default;
var Table = require('@tiptap/extension-table').default;
var TableRow = require('@tiptap/extension-table-row').default;
var TableCell = require('@tiptap/extension-table-cell').default;
var TableHeader = require('@tiptap/extension-table-header').default;
var TaskList = require('@tiptap/extension-task-list').default;
var TaskItem = require('@tiptap/extension-task-item').default;
var Suggestion = require('@tiptap/suggestion').default;
var PluginKey = require('@tiptap/pm/state').PluginKey;

var slashPluginKey = new PluginKey('taskSlashMenu');

var TaskImage = Image.extend({
    addAttributes: function() {
        return Object.assign({}, this.parent(), {
            'data-attachment-id': {
                default: null,
                parseHTML: function(element) {
                    return element.getAttribute('data-attachment-id');
                },
                renderHTML: function(attributes) {
                    if (!attributes['data-attachment-id']) {
                        return {};
                    }
                    return { 'data-attachment-id': attributes['data-attachment-id'] };
                }
            }
        });
    }
});

var TaskLink = Link.extend({
    addAttributes: function() {
        return Object.assign({}, this.parent(), {
            'data-attachment-id': {
                default: null,
                parseHTML: function(element) {
                    return element.getAttribute('data-attachment-id');
                },
                renderHTML: function(attributes) {
                    if (!attributes['data-attachment-id']) {
                        return {};
                    }
                    return { 'data-attachment-id': attributes['data-attachment-id'] };
                }
            }
        });
    }
});

var TaskMention = Mention.extend({
    parseHTML: function() {
        return [
            { tag: 'span.mention[data-id]' },
            { tag: 'span.mention[data-user-id]' }
        ];
    },
    addAttributes: function() {
        return Object.assign({}, this.parent(), {
            id: {
                default: null,
                parseHTML: function(element) {
                    return element.getAttribute('data-id') || element.getAttribute('data-user-id');
                },
                renderHTML: function(attributes) {
                    if (!attributes.id) {
                        return {};
                    }
                    return { 'data-id': attributes.id };
                }
            },
            label: {
                default: null,
                parseHTML: function(element) {
                    var fromAttr = element.getAttribute('data-value') || element.getAttribute('data-label');
                    if (fromAttr) {
                        return fromAttr;
                    }
                    return (element.textContent || '').replace(/^@/, '');
                },
                renderHTML: function(attributes) {
                    if (!attributes.label) {
                        return {};
                    }
                    return { 'data-label': attributes.label };
                }
            }
        });
    }
});

var TaskVideo = Node.create({
    name: 'taskVideo',
    group: 'block',
    atom: true,
    addAttributes: function() {
        return {
            src: { default: null },
            'data-attachment-id': { default: null },
            title: { default: null }
        };
    },
    parseHTML: function() {
        return [{ tag: 'video[data-attachment-id]' }];
    },
    renderHTML: function(props) {
        return ['video', mergeAttributes(props.HTMLAttributes, {
            controls: true,
            preload: 'metadata',
            playsinline: 'true',
            class: 'comment-inline-video'
        })];
    }
});

var UploadPlaceholder = Node.create({
    name: 'uploadPlaceholder',
    group: 'inline',
    inline: true,
    atom: true,
    addAttributes: function() {
        return { id: { default: null } };
    },
    parseHTML: function() {
        return [{ tag: 'span[data-upload-placeholder]' }];
    },
    renderHTML: function(props) {
        return ['span', {
            'data-upload-placeholder': props.HTMLAttributes.id || '',
            class: 'task-upload-placeholder'
        }, 'Caricamento…'];
    }
});

function createFloatingMenu(className, labelOf) {
    return function() {
        var el = null;
        var selected = 0;

        function paint(props) {
            if (!el) {
                return;
            }
            el.innerHTML = '';
            if (!props.items.length) {
                el.style.display = 'none';
                return;
            }
            el.style.display = 'block';
            props.items.forEach(function(item, index) {
                var row = document.createElement('button');
                row.type = 'button';
                row.className = 'task-menu-item' + (index === selected ? ' is-selected' : '');
                row.textContent = labelOf(item);
                row.addEventListener('mousedown', function(event) {
                    event.preventDefault();
                    props.command(item);
                });
                el.appendChild(row);
            });
            var rect = props.clientRect ? props.clientRect() : null;
            if (rect) {
                el.style.left = rect.left + 'px';
                el.style.top = (rect.bottom + 4) + 'px';
            }
        }

        return {
            onStart: function(props) {
                selected = 0;
                el = document.createElement('div');
                el.className = className;
                document.body.appendChild(el);
                paint(props);
            },
            onUpdate: function(props) {
                selected = 0;
                paint(props);
            },
            onKeyDown: function(props) {
                if (!props.items.length) {
                    return false;
                }
                if (props.event.key === 'ArrowDown') {
                    selected = (selected + 1) % props.items.length;
                    paint(props);
                    return true;
                }
                if (props.event.key === 'ArrowUp') {
                    selected = (selected + props.items.length - 1) % props.items.length;
                    paint(props);
                    return true;
                }
                if (props.event.key === 'Enter') {
                    props.command(props.items[selected]);
                    return true;
                }
                return false;
            },
            onExit: function() {
                if (el && el.parentNode) {
                    el.parentNode.removeChild(el);
                }
                el = null;
            }
        };
    };
}

function slashItems(query) {
    var items = [
        { title: 'Titolo', run: function(editor, range) { editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run(); } },
        { title: 'Elenco', run: function(editor, range) { editor.chain().focus().deleteRange(range).toggleBulletList().run(); } },
        { title: 'Elenco numerato', run: function(editor, range) { editor.chain().focus().deleteRange(range).toggleOrderedList().run(); } },
        { title: 'Checklist', run: function(editor, range) { editor.chain().focus().deleteRange(range).toggleTaskList().run(); } },
        { title: 'Citazione', run: function(editor, range) { editor.chain().focus().deleteRange(range).toggleBlockquote().run(); } },
        { title: 'Codice', run: function(editor, range) { editor.chain().focus().deleteRange(range).toggleCodeBlock().run(); } }
    ];
    var q = (query || '').toLowerCase();
    if (!q) {
        return items;
    }
    return items.filter(function(item) {
        return item.title.toLowerCase().indexOf(q) !== -1;
    });
}

var SlashCommands = Extension.create({
    name: 'slashCommands',
    addProseMirrorPlugins: function() {
        var editor = this.editor;
        return [
            Suggestion({
                editor: editor,
                char: '/',
                pluginKey: slashPluginKey,
                items: function(args) {
                    return slashItems(args.query);
                },
                command: function(payload) {
                    payload.props.run(payload.editor, payload.range);
                },
                render: createFloatingMenu('task-slash-menu', function(item) { return item.title; })
            })
        ];
    }
});

export default {
    name: 'WysiwygEditor',

    components: {
        EditorContent: EditorContent
    },

    props: {
        value: { type: String, default: '' },
        users: { type: Array, default: function() { return []; } },
        placeholder: { type: String, default: 'Scrivi un commento...' },
        disabled: { type: Boolean, default: false },
        taskId: { type: [Number, String], default: null },
        mode: {
            type: String,
            default: 'comment',
            validator: function(v) { return ['comment', 'description'].indexOf(v) !== -1; }
        },
        minHeight: { type: String, default: null },
        initialMentionedUserIds: { type: Array, default: function() { return []; } },
        attachments: { type: Array, default: function() { return []; } }
    },

    data: function() {
        return {
            editor: null,
            editorTick: 0,
            isFocused: false,
            isDragover: false,
            uploadProgress: { active: false, done: 0, total: 0 },
            mentionedUserIds: [],
            internalUpdate: false,
            attachedFiles: [],
            uploadChain: Promise.resolve()
        };
    },

    watch: {
        value: function(newVal) {
            if (this.internalUpdate || !this.editor) {
                return;
            }
            if (newVal !== this.getHtml()) {
                this.setEditorHtml(newVal);
            }
        },
        attachments: function() {
            if (!this.editor || this.internalUpdate || this.isFocused) {
                return;
            }
            if (/\[(img|video|file):\d+\]/.test(this.value || '')) {
                this.setEditorHtml(this.value);
            }
        },
        disabled: function(newVal) {
            if (this.editor) {
                this.editor.setEditable(!newVal);
            }
        }
    },

    created: function() {
        this.initEditor();
    },

    beforeDestroy: function() {
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }
    },

    methods: {
        initEditor: function() {
            var self = this;
            this.mentionedUserIds = this.normalizeMentionIds(this.initialMentionedUserIds);

            this.editor = new Editor({
                editable: !this.disabled,
                content: this.prepareIncomingHtml(this.value),
                extensions: [
                    StarterKit.configure({
                        heading: { levels: [1, 2, 3] }
                    }),
                    Underline,
                    TaskLink.configure({
                        openOnClick: false,
                        autolink: true,
                        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' }
                    }),
                    TaskImage.configure({
                        inline: false,
                        allowBase64: false,
                        HTMLAttributes: { class: 'comment-inline-image' }
                    }),
                    TaskVideo,
                    UploadPlaceholder,
                    Table.configure({ resizable: false }),
                    TableRow,
                    TableHeader,
                    TableCell,
                    TaskList,
                    TaskItem.configure({ nested: true }),
                    Placeholder.configure({ placeholder: this.placeholder }),
                    TaskMention.configure({
                        HTMLAttributes: { class: 'mention' },
                        renderHTML: function(props) {
                            var label = props.node.attrs.label || props.node.attrs.id || '';
                            return ['span', {
                                class: 'mention',
                                'data-id': props.node.attrs.id,
                                'data-user-id': props.node.attrs.id,
                                'data-value': label,
                                'data-denotation-char': '@'
                            }, '@' + label];
                        },
                        suggestion: {
                            items: function(args) {
                                var q = (args.query || '').toLowerCase();
                                return self.getMentionUsers().filter(function(user) {
                                    return !q || user.value.toLowerCase().indexOf(q) !== -1;
                                }).slice(0, 10).map(function(user) {
                                    return { id: String(user.id), label: user.value };
                                });
                            },
                            render: createFloatingMenu('task-mention-menu', function(item) { return item.label; })
                        }
                    }),
                    SlashCommands
                ],
                editorProps: {
                    transformPastedHTML: function(html) {
                        return normalizePastedHtml(extractDataImages(html).html);
                    },
                    handlePaste: function(view, event) {
                        return self.handlePaste(event);
                    },
                    handleKeyDown: function(view, event) {
                        if (event.key === 'Enter' && event.shiftKey) {
                            self.$emit('submit');
                            return true;
                        }
                        return false;
                    },
                    handleClick: function(view, pos, event) {
                        var link = event.target && event.target.closest ? event.target.closest('a') : null;
                        if (link && link.href && (event.metaKey || event.ctrlKey)) {
                            window.open(link.href, '_blank', 'noopener,noreferrer');
                            return true;
                        }
                        return false;
                    }
                },
                onUpdate: function() {
                    self.editorTick++;
                    if (self.internalUpdate) {
                        return;
                    }
                    self.internalUpdate = true;
                    self.$emit('input', self.getHtml());
                    self.emitMentionsFromEditor();
                    self.syncAttachedFilesFromContent();
                    self.$nextTick(function() {
                        self.internalUpdate = false;
                    });
                },
                onSelectionUpdate: function() {
                    self.editorTick++;
                },
                onFocus: function() {
                    self.isFocused = true;
                    self.$emit('focus');
                },
                onBlur: function() {
                    setTimeout(function() {
                        if (!self.editor || self.editor.isFocused || self.uploadProgress.active) {
                            return;
                        }
                        self.isFocused = false;
                        self.$emit('blur');
                    }, 200);
                }
            });

            this.syncAttachedFilesFromContent();
            this.emitMentionsFromEditor();
        },

        isActive: function(name) {
            if (!this.editor) {
                return false;
            }
            this.editorTick;
            return this.editor.isActive(name);
        },

        getMentionUsers: function() {
            return this.users.map(function(user) {
                return { id: user.id, value: user.full_name || (user.name + ' ' + (user.surname || '')).trim() };
            });
        },

        collectMentionIdsFromEditor: function() {
            if (!this.editor) {
                return [];
            }
            var ids = [];
            var nodes = this.editor.view.dom.querySelectorAll('.mention[data-id], .mention[data-user-id]');
            for (var i = 0; i < nodes.length; i++) {
                var raw = nodes[i].getAttribute('data-id') || nodes[i].getAttribute('data-user-id');
                var id = parseInt(raw, 10);
                if (!isNaN(id) && id > 0 && ids.indexOf(id) === -1) {
                    ids.push(id);
                }
            }
            return ids;
        },

        getMentionedUserIds: function() {
            return this.collectMentionIdsFromEditor();
        },

        emitMentionsFromEditor: function() {
            var ids = this.collectMentionIdsFromEditor();
            if (this.mentionedUserIds.join(',') === ids.join(',')) {
                return;
            }
            this.mentionedUserIds = ids;
            this.$emit('mentioned', ids.slice());
        },

        normalizeMentionIds: function(ids) {
            if (!Array.isArray(ids)) {
                return [];
            }
            var out = [];
            for (var i = 0; i < ids.length; i++) {
                var n = parseInt(ids[i], 10);
                if (!isNaN(n) && n > 0 && out.indexOf(n) === -1) {
                    out.push(n);
                }
            }
            return out;
        },

        getAttachmentMap: function() {
            var map = {};
            (this.attachments || []).forEach(function(att) {
                map[att.id] = att;
            });
            return map;
        },

        prepareIncomingHtml: function(html) {
            if (!html) {
                return '';
            }
            var map = this.getAttachmentMap();
            return html.replace(/\[(img|video|file):(\d+)\]/g, function(marker, type, id) {
                var att = map[parseInt(id, 10)];
                if (!att) {
                    return marker;
                }
                var url = escapeAttr(att.download_url || att.url || '');
                var name = escapeAttr(att.file_name || 'file');
                if (type === 'img') {
                    return '<img data-attachment-id="' + id + '" src="' + url + '" alt="' + name + '" class="comment-inline-image">';
                }
                if (type === 'video') {
                    return '<video data-attachment-id="' + id + '" src="' + url + '" class="comment-inline-video"></video>';
                }
                return '<a data-attachment-id="' + id + '" href="' + url + '" class="task-inline-file">' + name + '</a>';
            });
        },

        setEditorHtml: function(html) {
            if (!this.editor) {
                return;
            }
            this.internalUpdate = true;
            this.editor.commands.setContent(this.prepareIncomingHtml(html || ''), false);
            this.syncAttachedFilesFromContent();
            var self = this;
            this.$nextTick(function() {
                self.internalUpdate = false;
            });
        },

        getHtml: function() {
            if (!this.editor) {
                return '';
            }
            var html = this.editor.getHTML();
            html = html.replace(/<span[^>]*data-upload-placeholder="[^"]*"[^>]*>[\s\S]*?<\/span>/g, '');
            if (html === '<p></p>' || html === '<p><br></p>') {
                return '';
            }
            return html;
        },

        getText: function() {
            if (!this.editor) {
                return '';
            }
            return this.editor.getText().trim();
        },

        isEmpty: function() {
            if (!this.editor) {
                return true;
            }
            if (/data-attachment-id=|<img\b|<video\b/.test(this.getHtml())) {
                return false;
            }
            return this.editor.isEmpty;
        },

        clear: function() {
            if (!this.editor) {
                return;
            }
            this.editor.commands.clearContent(true);
            this.mentionedUserIds = [];
            this.attachedFiles = [];
        },

        focus: function() {
            if (this.editor) {
                this.editor.commands.focus();
            }
        },

        waitForUploads: function() {
            return this.uploadChain || Promise.resolve();
        },

        formatBold: function() { this.editor && this.editor.chain().focus().toggleBold().run(); },
        formatItalic: function() { this.editor && this.editor.chain().focus().toggleItalic().run(); },
        formatUnderline: function() { this.editor && this.editor.chain().focus().toggleUnderline().run(); },
        formatStrike: function() { this.editor && this.editor.chain().focus().toggleStrike().run(); },
        formatBulletList: function() { this.editor && this.editor.chain().focus().toggleBulletList().run(); },
        formatOrderedList: function() { this.editor && this.editor.chain().focus().toggleOrderedList().run(); },
        formatTaskList: function() { this.editor && this.editor.chain().focus().toggleTaskList().run(); },
        formatBlockquote: function() { this.editor && this.editor.chain().focus().toggleBlockquote().run(); },
        formatCodeBlock: function() { this.editor && this.editor.chain().focus().toggleCodeBlock().run(); },

        insertLink: function() {
            if (!this.editor) {
                return;
            }
            var previous = this.editor.getAttributes('link').href || 'https://';
            var url = prompt('Inserisci URL:', previous);
            if (url === null) {
                return;
            }
            if (url === '') {
                this.editor.chain().focus().unsetLink().run();
                return;
            }
            this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        },

        triggerFileUpload: function() {
            this.$refs.fileInput.click();
        },

        handleFileSelect: function(e) {
            var files = filesFromFileList(e.target.files);
            if (files.length) {
                this.uploadFiles(files);
            }
            this.$refs.fileInput.value = '';
        },

        handleDragOver: function() { this.isDragover = true; },
        handleDragLeave: function() { this.isDragover = false; },

        handleDrop: function(e) {
            this.isDragover = false;
            var files = filesFromFileList(e.dataTransfer.files);
            if (files.length) {
                this.uploadFiles(files);
            }
        },

        handlePaste: function(event) {
            var clipboard = event.clipboardData;
            if (!clipboard || !this.editor) {
                return false;
            }

            var html = clipboard.getData('text/html') || '';
            var extracted = extractDataImages(html);
            var files = filesFromFileList(clipboard.files);
            if (!files.length && clipboard.items) {
                for (var i = 0; i < clipboard.items.length; i++) {
                    if (clipboard.items[i].kind === 'file') {
                        var pasted = clipboard.items[i].getAsFile();
                        if (pasted) {
                            files.push(pasted);
                        }
                    }
                }
            }
            if (!files.length) {
                files = extracted.files;
            }
            if (!files.length) {
                return false;
            }

            event.preventDefault();
            var cleaned = normalizePastedHtml(extracted.html);
            var plain = clipboard.getData('text/plain') || '';
            var hasText = plain.trim().length > 0 || (cleaned && cleaned.replace(/<[^>]+>/g, '').trim().length > 0);
            if (hasText && cleaned && cleaned.replace(/<[^>]+>/g, '').trim()) {
                this.editor.chain().focus().insertContent(cleaned).run();
            } else if (hasText && plain.trim() && !/<table[\s>]/i.test(cleaned || '')) {
                this.editor.chain().focus().insertContent(plain).run();
            }
            if (!this.taskId) {
                this.$emit('file-pasted', files);
                return true;
            }
            this.uploadFiles(files);
            return true;
        },

        attachmentContent: function(attachment) {
            var id = String(attachment.id);
            var url = attachment.download_url || attachment.url || '';
            var name = attachment.file_name || 'file';
            var mime = (attachment.mime_type || '').toLowerCase();
            if (mime.indexOf('image/') === 0) {
                return { type: 'image', attrs: { src: url, alt: name, 'data-attachment-id': id } };
            }
            if (mime.indexOf('video/') === 0) {
                return { type: 'taskVideo', attrs: { src: url, title: name, 'data-attachment-id': id } };
            }
            return '<a href="' + escapeAttr(url) + '" data-attachment-id="' + escapeAttr(id) + '" class="task-inline-file">' + escapeAttr(name) + '</a> ';
        },

        findPlaceholder: function(id) {
            var found = null;
            this.editor.state.doc.descendants(function(node, pos) {
                if (node.type.name === 'uploadPlaceholder' && node.attrs.id === id) {
                    found = { pos: pos, size: node.nodeSize };
                }
            });
            return found;
        },

        uploadFiles: function(files) {
            var self = this;
            var fileList = filesFromFileList(files);
            if (!fileList.length) {
                return Promise.resolve();
            }
            if (!this.taskId) {
                this.$emit('file-pasted', fileList);
                return Promise.resolve();
            }

            var run = function() {
                return self.performUpload(fileList);
            };
            this.uploadChain = this.uploadChain.then(run, run);
            return this.uploadChain;
        },

        performUpload: function(fileList) {
            var self = this;
            var placeholderId = 'up-' + Date.now() + '-' + Math.random().toString(36).slice(2);
            this.editor.chain().focus().insertContent({
                type: 'uploadPlaceholder',
                attrs: { id: placeholderId }
            }).run();

            return uploadTaskAttachmentsSequential({
                taskId: this.taskId,
                files: fileList,
                onProgress: function(progress) {
                    self.uploadProgress = {
                        active: progress.active,
                        done: progress.done,
                        total: progress.total
                    };
                }
            }).then(function(result) {
                result.succeeded.forEach(function(item) {
                    var content = self.attachmentContent(item.attachment);
                    var placeholder = self.findPlaceholder(placeholderId);
                    if (placeholder) {
                        self.editor.chain().focus()
                            .deleteRange({ from: placeholder.pos, to: placeholder.pos + placeholder.size })
                            .insertContentAt(placeholder.pos, content)
                            .run();
                    } else {
                        self.editor.chain().focus().insertContent(content).run();
                    }
                    self.$emit('file-uploaded', item.attachment);
                    if (item.attachment.mime_type && item.attachment.mime_type.indexOf('image/') === 0) {
                        self.$emit('image-uploaded', item.attachment);
                    }
                });
                if (result.failed.length) {
                    var names = result.failed.map(function(f) { return f.file.name; }).join(', ');
                    self.$emit('upload-error', 'Alcuni file non sono stati caricati: ' + names);
                }
            }).catch(function() {
                self.$emit('upload-error', 'Errore durante il caricamento');
            }).finally(function() {
                var placeholder = self.editor ? self.findPlaceholder(placeholderId) : null;
                if (placeholder && self.editor) {
                    self.editor.chain().focus()
                        .deleteRange({ from: placeholder.pos, to: placeholder.pos + placeholder.size })
                        .run();
                }
                self.uploadProgress = { active: false, done: 0, total: 0 };
                if (self.editor) {
                    self.$emit('input', self.getHtml());
                }
            });
        },

        syncAttachedFilesFromContent: function() {
            if (!this.editor) {
                return;
            }
            var self = this;
            var html = this.editor.getHTML();
            var found = {};
            var match;
            var attrRe = /<a\b[^>]*data-attachment-id="(\d+)"[^>]*>/g;
            while ((match = attrRe.exec(html)) !== null) {
                found[match[1]] = true;
            }
            var markerRe = /\[file:(\d+)\]/g;
            while ((match = markerRe.exec(html)) !== null) {
                found[match[1]] = true;
            }
            var attachmentMap = this.getAttachmentMap();
            this.attachedFiles = Object.keys(found).map(function(id) {
                var att = attachmentMap[parseInt(id, 10)];
                return {
                    id: parseInt(id, 10),
                    name: att ? att.file_name : 'File',
                    url: att ? (att.download_url || att.url) : '',
                    isImage: false,
                    isVideo: false,
                    icon: att ? (att.icon || self.getFileIcon(att.mime_type, att.file_name)) : 'fa fa-file-o'
                };
            });
        },

        getFileIcon: function(mimeType, fileName) {
            var mt = (mimeType || '').toLowerCase();
            var ext = (fileName || '').split('.').pop().toLowerCase();
            if (mt.indexOf('pdf') !== -1 || ext === 'pdf') return 'fa fa-file-pdf-o';
            if (mt.indexOf('excel') !== -1 || mt.indexOf('spreadsheet') !== -1 || ext === 'xls' || ext === 'xlsx' || ext === 'ods' || ext === 'csv' || ext === 'tsv') return 'fa fa-file-excel-o';
            if (mt.indexOf('word') !== -1 || ext === 'doc' || ext === 'docx' || ext === 'odt' || ext === 'rtf') return 'fa fa-file-word-o';
            if (mt.indexOf('powerpoint') !== -1 || mt.indexOf('presentation') !== -1 || ext === 'ppt' || ext === 'pptx' || ext === 'odp') return 'fa fa-file-powerpoint-o';
            if (mt.indexOf('zip') !== -1 || ext === 'zip' || ext === 'rar' || ext === '7z') return 'fa fa-file-archive-o';
            return 'fa fa-file-o';
        },

        previewFile: function(file) {
            if (file.url) {
                window.open(file.url, '_blank');
            }
        },

        removeAttachedFile: function(index) {
            var file = this.attachedFiles[index];
            if (file && this.editor) {
                var html = this.getHtml().replace(
                    new RegExp('<a\\b[^>]*data-attachment-id="' + file.id + '"[^>]*>[\\s\\S]*?<\\/a>', 'i'),
                    ''
                ).replace(new RegExp('\\[file:' + file.id + '\\]', 'g'), '');
                this.setEditorHtml(html);
                this.$emit('input', this.getHtml());
            }
            this.attachedFiles.splice(index, 1);
        }
    }
};
</script>

<style>
.wysiwyg-editor-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    border-radius: 8px;
}
.wysiwyg-editor-wrapper .wysiwyg-editor-container {
    width: 100%;
}
.wysiwyg-editor-wrapper .ProseMirror {
    font-family: 'Sofia Pro', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    padding: 8px 10px;
    color: #e0e0e0;
    background: #2a2a2a;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 0;
    min-height: var(--wysiwyg-min-height, 44px);
    max-height: 300px;
    overflow-y: auto;
    outline: none;
}
.wysiwyg-editor-wrapper.mode-description .ProseMirror {
    min-height: var(--wysiwyg-min-height, 100px);
}
.wysiwyg-editor-wrapper.is-focused .ProseMirror {
    border-color: rgba(238, 185, 63, 0.5);
}
.wysiwyg-editor-wrapper .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    color: rgba(255, 255, 255, 0.35);
    float: left;
    height: 0;
    pointer-events: none;
}
.wysiwyg-editor-wrapper .ProseMirror p,
.wysiwyg-editor-wrapper .ProseMirror ul,
.wysiwyg-editor-wrapper .ProseMirror ol {
    margin: 0 0 0.4em;
}
.wysiwyg-editor-wrapper .ProseMirror h2 {
    font-size: 1.15em;
    margin: 0.4em 0;
}
.wysiwyg-editor-wrapper .ProseMirror blockquote {
    border-left: 3px solid rgba(238, 185, 63, 0.7);
    margin: 0.4em 0;
    padding-left: 0.7em;
    color: rgba(255, 255, 255, 0.8);
}
.wysiwyg-editor-wrapper .ProseMirror pre {
    background: #1c1c1c;
    border-radius: 6px;
    padding: 8px 10px;
    overflow-x: auto;
}
.wysiwyg-editor-wrapper .ProseMirror a {
    color: #eeb93f;
}
.wysiwyg-editor-wrapper .ProseMirror img.comment-inline-image,
.wysiwyg-editor-wrapper .ProseMirror video.comment-inline-video {
    max-width: 100%;
    max-height: 420px;
    width: auto;
    height: auto;
    border-radius: 6px;
    display: block;
    margin: 8px 0;
    object-fit: contain;
}
.wysiwyg-editor-wrapper .ProseMirror table {
    border-collapse: collapse;
    width: 100%;
    margin: 6px 0;
}
.wysiwyg-editor-wrapper .ProseMirror td,
.wysiwyg-editor-wrapper .ProseMirror th {
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 4px 8px;
    vertical-align: top;
}
.wysiwyg-editor-wrapper .ProseMirror th {
    background: rgba(255, 255, 255, 0.06);
}
.wysiwyg-editor-wrapper .ProseMirror ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;
}
.wysiwyg-editor-wrapper .ProseMirror ul[data-type="taskList"] li {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}
.wysiwyg-editor-wrapper .ProseMirror .mention {
    background: rgba(238, 185, 63, 0.2);
    color: #eeb93f;
    padding: 1px 4px;
    border-radius: 3px;
    font-weight: 500;
}
.wysiwyg-editor-wrapper .task-upload-placeholder,
.wysiwyg-editor-wrapper .ProseMirror .task-upload-placeholder {
    color: rgba(255, 255, 255, 0.55);
    font-style: italic;
}
.task-mention-menu,
.task-slash-menu {
    position: fixed;
    z-index: 2147483650;
    min-width: 180px;
    max-height: 220px;
    overflow-y: auto;
    background: #2a2a2a;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    padding: 4px;
}
.task-mention-menu .task-menu-item,
.task-slash-menu .task-menu-item {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    color: #e0e0e0;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
}
.task-mention-menu .task-menu-item.is-selected,
.task-slash-menu .task-menu-item.is-selected,
.task-mention-menu .task-menu-item:hover,
.task-slash-menu .task-menu-item:hover {
    background: rgba(238, 185, 63, 0.15);
    color: #eeb93f;
}
.attached-files-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 10px;
    background: #2a2a2a;
    border-left: 1px solid rgba(255, 255, 255, 0.15);
    border-right: 1px solid rgba(255, 255, 255, 0.15);
}
.attached-file-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(102, 126, 234, 0.1);
    border-radius: 6px;
    padding: 6px 30px 6px 6px;
    cursor: pointer;
    max-width: 220px;
}
.attached-file-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #667eea;
}
.attached-file-name {
    font-size: 12px;
    color: #e0e0e0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.attached-file-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(220, 53, 69, 0.8);
    border: none;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
}
.wysiwyg-bottom-toolbar {
    background: #2a2a2a;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-top: none;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
}
.wysiwyg-bottom-toolbar .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-wrap: wrap;
}
.wysiwyg-bottom-toolbar .toolbar-icon-btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.5);
    padding: 4px 7px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    line-height: 1;
}
.wysiwyg-bottom-toolbar .toolbar-icon-btn:hover,
.wysiwyg-bottom-toolbar .toolbar-icon-btn.is-active {
    color: #eeb93f;
    background: rgba(238, 185, 63, 0.12);
}
.wysiwyg-bottom-toolbar .toolbar-sep {
    width: 1px;
    height: 16px;
    background: rgba(255, 255, 255, 0.15);
    margin: 0 4px;
}
.wysiwyg-editor-wrapper .drag-overlay {
    position: absolute;
    inset: 0;
    background: rgba(102, 126, 234, 0.2);
    border: 2px dashed #667eea;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    z-index: 3;
    gap: 6px;
}
.wysiwyg-editor-wrapper .upload-indicator {
    position: absolute;
    right: 8px;
    bottom: 40px;
    background: rgba(0, 0, 0, 0.75);
    color: #fff;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 12px;
    z-index: 2;
}
.wysiwyg-editor-wrapper.is-disabled .ProseMirror {
    opacity: 0.7;
}
</style>
